import { parseTranscriptionEvent } from "./dictationEvents";
import { isRecordedDictationSupported } from "@juisci/dictation-recorder";
import type { DictationHandle, TranscriptionCallbacks } from "./dictationTypes";

// ---------------------------------------------------------------------------
// OpenAI WebRTC transport for JuisciGPT voice dictation (gpt-realtime-whisper) —
// the FALLBACK provider; the local PCM recorder in `@juisci/dictation-recorder`
// (Gemini 3.5 Transcribe, pre-recorded) is the default.
//
// Given a mic MediaStream + a short-lived OpenAI ephemeral token, open a Realtime
// transcription connection directly to OpenAI: mic audio flows over the peer
// connection, transcript events arrive on a data channel. OpenAI streams
// INCREMENTAL deltas, so we accumulate them into the current-utterance hypothesis
// and emit the shared `{ kind: "partial" }` (replace) / `{ kind: "final" }` events.
// ---------------------------------------------------------------------------

const REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";
const DATA_CHANNEL_LABEL = "oai-events";
const CONNECT_TIMEOUT_MS = 15_000;

// Whether the runtime can capture mic audio and run at least one transport
// (the AudioWorklet recorder, or OpenAI over WebRTC). Native
// shells grant capture through the mic-permission bridge shipped in the
// released builds (iOS `requestMediaCapturePermissionFor` / Android
// `onPermissionRequest`); the plain web browser owns the prompt itself. A native
// build too old to have that bridge still passes this check but fails at capture
// time with a graceful permission-denied toast (`requestMicStream` throws
// `NotAllowedError`) — preferable to hiding the mic on every current release,
// which is what a synchronous native-capability flag did (no released build set
// it, so the mic vanished app-wide).
export function isDictationSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    navigator.mediaDevices !== undefined &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    (isRecordedDictationSupported() || typeof RTCPeerConnection !== "undefined")
  );
}

async function negotiate(
  pc: RTCPeerConnection,
  ephemeralToken: string
): Promise<void> {
  const offer = await pc.createOffer();
  await pc.setLocalDescription(offer);

  const offerSdp = offer.sdp;
  if (offerSdp === undefined) {
    throw new Error("Local SDP offer has no description");
  }

  const response = await fetch(REALTIME_CALLS_URL, {
    method: "POST",
    body: offerSdp,
    headers: {
      Authorization: `Bearer ${ephemeralToken}`,
      "Content-Type": "application/sdp",
    },
  });
  if (!response.ok) {
    throw new Error(`Realtime SDP exchange failed: ${String(response.status)}`);
  }

  const answerSdp = await response.text();
  await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
}

/**
 * Establish the transcription connection. Resolves with a handle once the data
 * channel is open (dictation is live); rejects if the handshake fails or times
 * out. After it resolves, an unexpected drop invokes `callbacks.onClosed`.
 */
export async function connectRealtimeTranscription(
  stream: MediaStream,
  ephemeralToken: string,
  callbacks: TranscriptionCallbacks
): Promise<DictationHandle> {
  const pc = new RTCPeerConnection();
  let torn = false;
  // Accumulates OpenAI's incremental deltas into the current-utterance hypothesis.
  let segment = "";

  const teardown = (): void => {
    if (torn) return;
    torn = true;
    pc.onconnectionstatechange = null;
    dataChannel.onopen = null;
    dataChannel.onmessage = null;
    try {
      pc.close();
    } catch {
      // Already closed — nothing to release.
    }
  };

  const handle: DictationHandle = { stop: teardown };

  for (const track of stream.getAudioTracks()) {
    pc.addTrack(track, stream);
  }

  // Create the data channel BEFORE the offer so its m-line is negotiated.
  const dataChannel = pc.createDataChannel(DATA_CHANNEL_LABEL);
  dataChannel.onmessage = (event) => {
    if (typeof event.data !== "string") return;
    const parsed = parseTranscriptionEvent(event.data);
    if (parsed === null) return;
    switch (parsed.kind) {
      case "delta": {
        segment = segment + parsed.text;
        callbacks.onEvent({ kind: "partial", text: segment });
        return;
      }
      case "final": {
        segment = "";
        callbacks.onEvent({ kind: "final", text: parsed.text });
        return;
      }
      case "error": {
        callbacks.onEvent({ kind: "error", message: parsed.message });
        return;
      }
      default:
        throw new Error(parsed satisfies never);
    }
  };

  return new Promise<DictationHandle>((resolve, reject) => {
    let settled = false;

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      teardown();
      reject(new Error("Realtime connection timed out"));
    }, CONNECT_TIMEOUT_MS);

    dataChannel.onopen = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve(handle);
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      // `disconnected` is a transient ICE blip that usually recovers to
      // `connected`; only `failed`/`closed` are terminal.
      if (state !== "failed" && state !== "closed") {
        return;
      }
      if (!settled) {
        settled = true;
        clearTimeout(timeout);
        teardown();
        reject(new Error(`Realtime connection ${state}`));
        return;
      }
      if (!torn) {
        teardown();
        callbacks.onClosed("connection-lost");
      }
    };

    void negotiate(pc, ephemeralToken).catch((error: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      teardown();
      reject(error instanceof Error ? error : new Error(String(error)));
    });
  });
}
